import { TaxType as AmbassadorTaxType } from '@wix/ambassador-events-v1-event/types';
import { FeeType as FeeTypeAmbassador } from '@wix/ambassador-events-v1-ticket-definition/types';
import { FeeName, FeeType, TaxType } from '@wix/events-types';
import { WIX_FEE_RATE } from '../constants/event';
import { getCurrencyFormatConfig, getFormattedMoney } from '../price/formatter';
import { getTicketDefinitionCurrency, isDonationTicketDefinition } from './ticket-definition';
export const getAddedFee = (invoice) => {
    const fees = invoice.fees;
    return fees === null || fees === void 0 ? void 0 : fees.find(({ type }) => isFeeAdded(type));
};
export const getIncludedFee = (invoice) => {
    const fees = invoice.fees;
    return fees === null || fees === void 0 ? void 0 : fees.find(({ type }) => isFeeIncluded(type));
};
export const isFeeAdded = (type) => [
    FeeType.FEE_ADDED,
    FeeType.FEE_ADDED_AT_CHECKOUT,
    FeeTypeAmbassador.FEE_ADDED,
    FeeTypeAmbassador.FEE_ADDED_AT_CHECKOUT,
].includes(type);
export const isFeeIncluded = (type) => [FeeType.FEE_INCLUDED, FeeType.FEE_INCLUDED, FeeTypeAmbassador.FEE_INCLUDED, FeeTypeAmbassador.FEE_INCLUDED].includes(type);
export const getPaidPlanDiscount = (invoice) => { var _a, _b; return (_b = (_a = invoice === null || invoice === void 0 ? void 0 : invoice.discount) === null || _a === void 0 ? void 0 : _a.discounts) === null || _b === void 0 ? void 0 : _b.find(item => item.paidPlan); };
export const getCouponDiscount = (invoice) => { var _a, _b; return (_b = (_a = invoice === null || invoice === void 0 ? void 0 : invoice.discount) === null || _a === void 0 ? void 0 : _a.discounts) === null || _b === void 0 ? void 0 : _b.find(item => item.coupon); };
export const getCouponDiscountCode = (invoice) => { var _a, _b; return (_b = (_a = getCouponDiscount(invoice)) === null || _a === void 0 ? void 0 : _a.coupon) === null || _b === void 0 ? void 0 : _b.code; };
export const getCouponDiscountName = (invoice) => { var _a, _b; return (_b = (_a = getCouponDiscount(invoice)) === null || _a === void 0 ? void 0 : _a.coupon) === null || _b === void 0 ? void 0 : _b.name; };
export const getCouponDiscountId = (invoice) => { var _a, _b; return (_b = (_a = getCouponDiscount(invoice)) === null || _a === void 0 ? void 0 : _a.coupon) === null || _b === void 0 ? void 0 : _b.couponId; };
export const getCouponDiscountAmount = (invoice) => { var _a; return (_a = getCouponDiscount(invoice)) === null || _a === void 0 ? void 0 : _a.amount; };
export const hasPaidPlanDiscount = (invoice) => Boolean(getPaidPlanDiscount(invoice));
export const hasCouponDiscount = (invoice) => Boolean(getCouponDiscount(invoice));
export const hasDiscount = (invoice) => { var _a, _b; return Boolean(Number((_b = (_a = invoice === null || invoice === void 0 ? void 0 : invoice.discount) === null || _a === void 0 ? void 0 : _a.amount) === null || _b === void 0 ? void 0 : _b.amount)); };
export const hasTax = (invoice) => { var _a; return Boolean(Number((_a = invoice.tax) === null || _a === void 0 ? void 0 : _a.amount.amount)); };
export const isTaxAdded = (type) => [TaxType.ADDED, TaxType.ADDED_AT_CHECKOUT, AmbassadorTaxType.ADDED, AmbassadorTaxType.ADDED_AT_CHECKOUT].includes(type);
export const hasAddedFee = (invoice) => { var _a; return Boolean(Number((_a = getAddedFee(invoice)) === null || _a === void 0 ? void 0 : _a.amount.amount)); };
export const hasIncludedFee = (invoice) => { var _a; return Boolean(Number((_a = getIncludedFee(invoice)) === null || _a === void 0 ? void 0 : _a.amount.amount)); };
export const hasSubtotal = (invoice) => hasDiscount(invoice) || hasTax(invoice) || hasAddedFee(invoice);
export const getPercentOffRate = (paidPlan) => { var _a, _b; return (_b = (_a = paidPlan.discount) === null || _a === void 0 ? void 0 : _a.percentOffRate) !== null && _b !== void 0 ? _b : '0'; };
export const getCheckoutSummary = (invoice, moneyFormatter = getFormattedMoney) => {
    var _a;
    return ({
        subtotal: hasSubtotal(invoice) ? moneyFormatter(invoice.subTotal) : null,
        paidPlanDiscount: hasPaidPlanDiscount(invoice) ? `-${moneyFormatter(getPaidPlanDiscount(invoice).amount)}` : null,
        discount: hasCouponDiscount(invoice) ? `-${moneyFormatter(getCouponDiscount(invoice).amount)}` : null,
        tax: hasTax(invoice) ? moneyFormatter(invoice.tax.amount) : null,
        addedFee: hasAddedFee(invoice) ? moneyFormatter(getAddedFee(invoice).amount) : null,
        includedFee: hasIncludedFee(invoice) ? moneyFormatter(getIncludedFee(invoice).amount) : null,
        total: moneyFormatter(invoice.grandTotal),
        ticketTotal: moneyFormatter(invoice.subTotal),
        revenue: moneyFormatter((_a = invoice.revenue) !== null && _a !== void 0 ? _a : {}),
    });
};
const roundPrice = (amount, currencyCode) => { var _a, _b; return amount.toFixed((_b = (_a = getCurrencyFormatConfig(currencyCode)) === null || _a === void 0 ? void 0 : _a.decimalDigits) !== null && _b !== void 0 ? _b : 2); };
export const getCurrencyFromTickets = (ticketDefinitions) => ticketDefinitions.length ? getTicketDefinitionCurrency(ticketDefinitions[0]) : '';
export const getPreliminaryInvoice = (ticketDefinitions, selectedItems, taxConfig, paidPlan) => {
    const currency = getCurrencyFromTickets(ticketDefinitions);
    let items = getPreliminaryItems(ticketDefinitions, selectedItems, currency);
    const subTotalAmount = calculateSubTotal(items);
    const subTotal = getMoney(subTotalAmount, currency);
    let grandTotalAmount = Number(subTotal.amount);
    const { discount, items: newItems, grandTotalAmount: updatedGrandTotalAmount, } = calculatePaidPlanDiscount(paidPlan, items, grandTotalAmount);
    items = newItems;
    grandTotalAmount = updatedGrandTotalAmount;
    items = appendTaxAndFeesToItems(items, ticketDefinitions, taxConfig, currency);
    const taxAmount = calculateTax(items);
    const taxableAmount = calculateTaxable(items);
    const addedFeeAmount = calculateAddedFee(items);
    const includedFeeAmount = calculateIncludedFee(items);
    grandTotalAmount += addedFeeAmount;
    if (isTaxAdded(taxConfig === null || taxConfig === void 0 ? void 0 : taxConfig.type)) {
        grandTotalAmount = grandTotalAmount + taxAmount;
    }
    else if ((taxConfig === null || taxConfig === void 0 ? void 0 : taxConfig.type) === TaxType.INCLUDED) {
        subTotal.value = subTotal.amount = roundPrice(Number(subTotal.amount) - taxAmount, currency);
    }
    const fees = [];
    if (addedFeeAmount) {
        // TODO: What to do here? (probably will need to change to FeeType.FEE_ADDED_AT_CHECKOUT after dashboard is changed to create with this FeeType)
        fees.push(getFee(FeeType.FEE_ADDED, getMoney(addedFeeAmount, currency)));
    }
    if (includedFeeAmount) {
        fees.push(getFee(FeeType.FEE_INCLUDED, getMoney(includedFeeAmount, currency)));
    }
    const grandTotal = getMoney(grandTotalAmount, currency);
    return {
        items: convertExtendedItemsToItems(items),
        discount,
        subTotal,
        grandTotal,
        tax: taxConfig
            ? Object.assign(Object.assign({}, taxConfig), { amount: getMoney(taxAmount, currency), taxable: getMoney(taxableAmount, currency) }) : null,
        fees,
    };
};
const getEventCurrencyFromItems = (items) => { var _a, _b; return (_b = (_a = items === null || items === void 0 ? void 0 : items[0]) === null || _a === void 0 ? void 0 : _a.price.currency) !== null && _b !== void 0 ? _b : 'USD'; };
const orderItemsByPrice = (items) => [...items].sort((a, b) => {
    var _a, _b, _c, _d;
    const aPrice = Number((_b = (_a = a === null || a === void 0 ? void 0 : a.price) === null || _a === void 0 ? void 0 : _a.amount) !== null && _b !== void 0 ? _b : 0);
    const bPrice = Number((_d = (_c = b === null || b === void 0 ? void 0 : b.price) === null || _c === void 0 ? void 0 : _c.amount) !== null && _d !== void 0 ? _d : 0);
    return bPrice - aPrice;
});
const getAmountPercent = (amount, percentOffRate, currencyCode) => roundPrice((Number(amount) / 100) * Number(percentOffRate), currencyCode);
const ensureDiscountExistence = (currentDiscount, defaultCurrency, currentTotalAmount) => currentDiscount || {
    discounts: [],
    amount: { amount: '0', currency: defaultCurrency },
    afterDiscount: { amount: currentTotalAmount, currency: defaultCurrency },
};
const addPaidPlanDiscount = (discount, name, amount, currency, quantityDiscounted, rate) => {
    const newDiscount = Object.assign(Object.assign({}, discount), { amount: Object.assign({}, discount.amount), afterDiscount: Object.assign({}, discount.afterDiscount), discounts: [...discount.discounts] });
    newDiscount.discounts.push({
        amount: { amount, currency },
        paidPlan: {
            name,
            percentDiscount: {
                quantityDiscounted,
                rate,
            },
        },
    });
    newDiscount.amount.amount = roundPrice(Number(newDiscount.amount.amount) + Number(amount), currency);
    newDiscount.afterDiscount.amount = roundPrice(Number(newDiscount.afterDiscount.amount) - Number(amount), currency);
    return newDiscount;
};
const calculatePaidPlanDiscount = (paidPlan, items, grandTotalAmount, discount) => {
    if (!paidPlan) {
        return {
            items,
            discount,
            grandTotalAmount,
        };
    }
    const { name, discount: { percentOffRate }, } = paidPlan;
    const currency = getEventCurrencyFromItems(items);
    const orderedItems = orderItemsByPrice(items);
    let quantitySum = 0;
    let discountAmountSum = 0;
    let { remainingCredits } = paidPlan;
    orderedItems.forEach(orderedItem => {
        if (remainingCredits && !orderedItem.isDonation) {
            const { total: { amount: totalAmount }, } = orderedItem;
            let totalDiscountForItem = 0;
            let totalTicketsDiscountedForItem = 0;
            orderedItem.ticketPrices = orderedItem.ticketPrices.map(ticketPrice => {
                if (!remainingCredits) {
                    return ticketPrice;
                }
                const ticketDiscountAmount = Number(getAmountPercent(ticketPrice, percentOffRate, currency));
                remainingCredits -= 1;
                totalTicketsDiscountedForItem += 1;
                totalDiscountForItem += ticketDiscountAmount;
                return ticketPrice - ticketDiscountAmount;
            });
            quantitySum += totalTicketsDiscountedForItem;
            discountAmountSum += totalDiscountForItem;
            orderedItem.discount = ensureDiscountExistence(orderedItem.discount, currency, totalAmount);
            orderedItem.discount = addPaidPlanDiscount(orderedItem.discount, name, roundPrice(totalDiscountForItem, currency), currency, totalTicketsDiscountedForItem, getPercentOffRate(paidPlan));
        }
    });
    let grandTotalAfterDiscount = grandTotalAmount;
    if (quantitySum) {
        discount = ensureDiscountExistence(discount, currency, roundPrice(grandTotalAmount, currency));
        discount = addPaidPlanDiscount(discount, name, roundPrice(discountAmountSum, currency), currency, quantitySum, getPercentOffRate(paidPlan));
        grandTotalAfterDiscount = grandTotalAfterDiscount - Number(discountAmountSum);
    }
    return {
        items,
        discount,
        grandTotalAmount: grandTotalAfterDiscount,
    };
};
const appendTaxAndFeesToItems = (items, ticketDefinitions, taxConfig, currency) => items.map(item => {
    const { wixFeeConfig } = ticketDefinitions.find(def => def.id === item.id);
    const taxesAndFees = item.ticketPrices.map(ticketPrice => {
        const ticketTax = getPreliminaryTax(taxConfig, ticketPrice, currency, item.isDonation);
        const subtotal = isTaxAdded(ticketTax === null || ticketTax === void 0 ? void 0 : ticketTax.type) ? ticketPrice + Number(ticketTax.amount.amount) : ticketPrice;
        const ticketFees = getPreliminaryFees(wixFeeConfig, subtotal, currency);
        return { tax: ticketTax, fees: ticketFees };
    });
    const { tax, fees } = taxesAndFees.reduce(({ tax: taxItem, fees: feesItem }, { tax: taxSum, fees: feesSum }) => {
        if (taxSum) {
            taxSum.taxable.value = taxSum.taxable.amount = roundPrice(Number(taxSum.taxable.amount) + Number(taxItem.taxable.amount), currency);
            taxSum.amount.value = taxSum.amount.amount = roundPrice(Number(taxSum.amount.amount) + Number(taxItem.amount.amount), currency);
        }
        if (feesSum.length) {
            feesSum[0].amount.value = feesSum[0].amount.amount = roundPrice(Number(feesSum[0].amount.amount) + Number(feesItem[0].amount.amount), currency);
        }
        return { tax: taxSum, fees: feesSum };
    });
    return Object.assign(Object.assign({}, item), { tax,
        fees });
});
const convertExtendedItemsToItems = (extendedItems) => extendedItems.map(extendedItem => {
    const newItem = {};
    const bannedKeys = ['ticketPrices', 'isDonation'];
    Object.keys(extendedItem).forEach(key => {
        if (!bannedKeys.includes(key)) {
            // @ts-expect-error
            newItem[key] = extendedItem[key];
        }
    });
    return newItem;
});
const getPreliminaryItems = (ticketDefinitions, selectedItems, currency) => {
    const picks = Object.entries(selectedItems).filter(([, { quantity }]) => Boolean(quantity));
    return picks
        .map(([ticketDefId, { quantity, priceOverrides }]) => {
        const ticketDefinition = ticketDefinitions.find(def => def.id === ticketDefId);
        const { id, name, price } = ticketDefinition;
        const isDonation = isDonationTicketDefinition(ticketDefinition);
        if (priceOverrides === null || priceOverrides === void 0 ? void 0 : priceOverrides.length) {
            const groupedPrices = Object.values(priceOverrides.reduce((groups, cur) => (Object.assign(Object.assign({}, groups), { [cur]: [...(groups[cur] || []), cur] })), {}));
            return groupedPrices.map(ticketPrices => ({
                id,
                name,
                price: getMoney(Number(ticketPrices[0]), currency),
                quantity: ticketPrices.length,
                total: getMoney(Number(ticketPrices[0]) * ticketPrices.length, currency),
                ticketPrices: ticketPrices.map(Number),
                isDonation,
            }));
        }
        else {
            const ticketPrice = Number(price.amount);
            return [
                {
                    id,
                    name,
                    price: getMoney(ticketPrice, currency),
                    quantity,
                    total: getMoney(Number(ticketPrice) * quantity, currency),
                    ticketPrices: Array(quantity).fill(ticketPrice),
                    isDonation,
                },
            ];
        }
    })
        .flat();
};
export const getPreliminaryTax = (taxConfig, total, currency, isDonation = false) => {
    if (taxConfig) {
        if (isDonation && !taxConfig.appliesToDonations) {
            return null;
        }
        const tax = {
            type: taxConfig.type,
            name: taxConfig.name,
            rate: taxConfig.rate,
        };
        const rateAmount = Number(tax.rate);
        if (isTaxAdded(tax.type)) {
            tax.taxable = getMoney(total, currency);
            tax.amount = getMoney((total * rateAmount) / 100, currency);
        }
        else {
            tax.taxable = getMoney((total * 100) / (100 + rateAmount), currency);
            tax.amount = getMoney(total - Number(tax.taxable.amount), currency);
        }
        return tax;
    }
    return null;
};
const getPreliminaryFees = (wixFeeConfig, total, currency) => wixFeeConfig ? [getFee(wixFeeConfig.type, getMoney(total * WIX_FEE_RATE, currency))] : [];
const calculateSubTotal = (items) => items.reduce((amount, item) => amount + Number(item.total.amount), 0);
const calculateTax = (items) => items.reduce((amount, item) => { var _a, _b; return amount + Number((_b = (_a = item.tax) === null || _a === void 0 ? void 0 : _a.amount.amount) !== null && _b !== void 0 ? _b : 0); }, 0);
const calculateTaxable = (items) => items.reduce((amount, item) => { var _a, _b; return amount + Number((_b = (_a = item.tax) === null || _a === void 0 ? void 0 : _a.taxable.amount) !== null && _b !== void 0 ? _b : 0); }, 0);
const calculateAddedFee = (items) => items.reduce((amount, item) => { var _a; return (isFeeAdded((_a = item.fees[0]) === null || _a === void 0 ? void 0 : _a.type) ? amount + Number(item.fees[0].amount.amount) : amount); }, 0);
const calculateIncludedFee = (items) => items.reduce((amount, item) => { var _a; return ((_a = item.fees[0]) === null || _a === void 0 ? void 0 : _a.type) === FeeType.FEE_INCLUDED ? amount + Number(item.fees[0].amount.amount) : amount; }, 0);
const getMoney = (amount, currency) => {
    const value = roundPrice(amount, currency);
    return {
        value,
        amount: value,
        currency,
    };
};
const getFee = (type, amount) => ({
    type,
    amount,
    name: FeeName.WIX_FEE,
    rate: (WIX_FEE_RATE * 100).toString(),
});
export const getTaxLabel = (invoice) => hasTax(invoice) ? `${invoice.tax.name} (${invoice.tax.rate}%)` : null;
export const getMembershipLabel = (invoice) => hasPaidPlanDiscount(invoice) ? getPaidPlanDiscount(invoice).paidPlan.name : null;
export const getPaidPlanPercentDiscount = (invoice) => hasPaidPlanDiscount(invoice) ? Number(getPaidPlanDiscount(invoice).paidPlan.percentDiscount.rate).toString() : null;
//# sourceMappingURL=checkout.js.map